import React from "react";
import {
  Play,
  Pencil,
  CheckCircle,
  Archive,
  Trash2,
  Copy,
  RotateCcw,
  MinusCircle,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { TestPlanStatusBadge } from "./TestPlanStatusBadge";

function formatRange(start, end) {
  const fmt = (d) => (d ? String(d).replace(/-/g, "/") : "–");
  if (!start && !end) return "–";
  return `${fmt(start)} - ${fmt(end)}`;
}

export function TestPlanTable({
  rows,
  selectedPlans,
  onToggleRow,
  onSelectAll,
  allSelected,
  someSelected,
  onRun,
  onEdit,
  onComplete,
  onArchive,
  onClone,
  onDelete,
  onRestore,
  onReferenceClick,
  onTestRunsClick,
}) {
  return (
    <div
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      data-testid="test-plan-table"
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/80">
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={onSelectAll}
                  aria-label="Select all plans"
                />
              </th>
              <th className="px-4 py-3 font-semibold text-text-primary">
                Plan Name
              </th>
              <th className="px-4 py-3 font-semibold text-text-primary">
                Reference
              </th>
              <th className="px-4 py-3 font-semibold text-text-primary">
                Start Date - End Date
              </th>
              <th className="px-4 py-3 font-semibold text-text-primary">
                Test Runs
              </th>
              <th className="px-4 py-3 font-semibold text-text-primary">
                Status
              </th>
              <th className="px-4 py-3 font-semibold text-text-primary">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const checked = selectedPlans.includes(row.id);
              return (
                <tr
                  key={row.id}
                  className={`border-b border-gray-100 last:border-0 ${
                    checked ? "bg-violet-50/40" : "hover:bg-gray-50/50"
                  }`}
                >
                  <td className="px-4 py-4 align-top">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={checked}
                      onChange={() => onToggleRow(row.id)}
                      aria-label={`Select ${row.name}`}
                    />
                  </td>
                  <td className="max-w-[220px] px-4 py-4 align-top">
                    <div className="font-semibold text-text-primary">
                      {row.name}
                    </div>
                    {row.description ? (
                      <div className="mt-0.5 line-clamp-2 text-xs text-text-secondary">
                        {row.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="flex flex-wrap gap-x-1 gap-y-0.5">
                      {(row.reference || []).length === 0 ? (
                        <span className="text-text-secondary">–</span>
                      ) : (
                        (row.reference || []).map((ref, idx) => (
                          <span
                            key={`${row.id}-ref-${idx}`}
                            className="inline-flex items-center text-sm"
                          >
                            {idx > 0 ? (
                              <span className="mr-1 text-text-secondary">
                                ,
                              </span>
                            ) : null}
                            <button
                              type="button"
                              className="text-blue-600 underline-offset-2 hover:underline"
                              onClick={() => onReferenceClick?.(row, ref)}
                            >
                              {ref}
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 align-top text-text-primary">
                    {formatRange(row.startDate, row.endDate)}
                  </td>
                  <td className="px-4 py-4 align-top">
                    {(row.testRunLabels || []).length === 0 ? (
                      <span className="text-text-secondary">0</span>
                    ) : (
                      <div className="flex flex-wrap gap-x-1">
                        {(row.testRunLabels || []).map((label, idx) => (
                          <span
                            key={label}
                            className="inline-flex items-center text-sm"
                          >
                            {idx > 0 ? (
                              <span className="mr-1 text-text-secondary">
                                ,
                              </span>
                            ) : null}
                            <button
                              type="button"
                              className="text-blue-600 underline-offset-2 hover:underline"
                              onClick={() => onTestRunsClick?.(row, label)}
                            >
                              {label}
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 align-top">
                    <TestPlanStatusBadge status={row.status} />
                  </td>
                  <td className="px-4 py-4 align-top">
                    <RowActions
                      status={row.status}
                      onRun={() => onRun(row)}
                      onEdit={() => onEdit(row)}
                      onComplete={() => onComplete(row)}
                      onArchive={() => onArchive(row)}
                      onClone={() => onClone(row)}
                      onDelete={() => onDelete(row)}
                      onRestore={() => onRestore(row)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RowActions({
  status,
  onRun,
  onEdit,
  onComplete,
  onArchive,
  onClone,
  onDelete,
  onRestore,
}) {
  const s = (status || "").toUpperCase();
  const iconBtn =
    "h-8 w-8 shrink-0 rounded-lg border border-transparent p-0 text-gray-600 hover:bg-gray-100 hover:text-gray-900";

  if (s === "ACTIVE") {
    return (
      <div
        className="flex flex-wrap items-center gap-1"
        data-testid="row-actions-active"
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconBtn}
          onClick={onRun}
          title="Run"
        >
          <Play className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconBtn}
          onClick={onEdit}
          title="Edit"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconBtn}
          onClick={onComplete}
          title="Complete"
        >
          <CheckCircle className="h-4 w-4 text-emerald-600" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconBtn}
          onClick={onArchive}
          title="Archive"
        >
          <Archive className="h-4 w-4 text-rose-600" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconBtn}
          onClick={onClone}
          title="Clone"
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  if (s === "COMPLETED") {
    return (
      <div
        className="flex flex-wrap items-center gap-1"
        data-testid="row-actions-completed"
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconBtn}
          onClick={onDelete}
          title="Delete"
        >
          <MinusCircle className="h-4 w-4 text-rose-600" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={iconBtn}
          onClick={onClone}
          title="Clone"
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  return (
    <div
      className="flex flex-wrap items-center gap-1"
      data-testid="row-actions-archived"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={iconBtn}
        onClick={onRestore}
        title="Restore"
      >
        <RotateCcw className="h-4 w-4 text-amber-700" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={iconBtn}
        onClick={onDelete}
        title="Delete"
      >
        <Trash2 className="h-4 w-4 text-rose-600" />
      </Button>
    </div>
  );
}
